import Vue from 'vue';
import Vuex, { Commit } from 'vuex';
import axios from 'axios';
import { getCookie } from '../utils/cookies';

Vue.use(Vuex);

// Note: For local testing purposes, there is a mock login store at ./loginStoreMock.ts.

type LoginState = {
	isLoggedIn: boolean;
	sessionExpires: number;
	hasReadNote: boolean;
	hasReadTip: boolean;
	isRDH: boolean;
	redirectToLogoutPage: boolean;
};

/**
 * Generic post request function
 * @param commit
 * @param state
 * @param url
 * @param action
 */

async function postRequest(commit: Commit, state: LoginState, url: string, action: string) {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(axios.defaults as { headers?: any }).headers.common['X-XSRF-TOKEN'] = getCookie('XSRF-TOKEN');
	try {
		const result: {
			data: {
				maxInactiveInterval: string;
			};
		} = await axios.post(
			`${url}/fb`,
			{
				action: `${action}`,
				data: {},
			},
			{
				headers: { 'X-Requested-With': 'XMLHttpRequest' },
				withCredentials: true,
			},
		);

		commit('SET_LOGIN_STATE', {
			...state,
			sessionExpires: result.data.maxInactiveInterval,
		});
	} catch (err) {
		commit('SET_LOGIN_STATE', {
			...state,
			isLoggedIn: false,
			sessionExpires: 0,
			isRDH: false,
		});
	}
}

export const loginStore = {
	namespaced: true,
	state: {
		isLoggedIn: false,
		sessionExpires: 0,
		hasReadNote: false,
		hasReadTip: false,
		isRDH: false,
		redirectToLogoutPage: true,
	},
	mutations: {
		SET_LOGIN_STATE(state: LoginState, payload: LoginState) {
			state.isLoggedIn = payload.isLoggedIn;
			state.sessionExpires = payload.sessionExpires;
			state.isRDH = payload.isRDH;
			state.redirectToLogoutPage = payload.redirectToLogoutPage;
		},
		SET_HAS_READ_NOTE(state: LoginState, payload: LoginState) {
			state.hasReadNote = payload.hasReadNote;
		},
		SET_HAS_READ_TIP(state: LoginState, payload: LoginState) {
			state.hasReadTip = payload.hasReadTip;
		},
		SET_IS_RDH(state: LoginState, payload: LoginState) {
			state.isRDH = payload.isRDH;
		},
	},
	actions: {
		hasReadNote({ commit, state }: { commit: Commit; state: LoginState }, hasReadNote: boolean) {
			commit('SET_HAS_READ_NOTE', {
				...state,
				hasReadNote,
			});
		},
		updateHasReadTip({ commit, state }: { commit: Commit; state: LoginState }, hasReadTip: boolean) {
			commit('SET_HAS_READ_TIP', {
				...state,
				hasReadTip,
			});
		},
		updateLoginState({ commit, state }: { commit: Commit; state: LoginState }, payload: LoginState) {
			commit('SET_LOGIN_STATE', {
				...state,
				...payload,
			});
		},
		updateIsRDH({ commit, state }: { commit: Commit; state: LoginState }, isRDH: boolean) {
			commit('SET_IS_RDH', {
				...state,
				isRDH,
			});
		},
		async updateSession({ commit, state }: { commit: Commit; state: LoginState }, url: string) {
			if (url) {
				const action = 'pingTest';
				postRequest(commit, state, url, action);
			}
		},
		/**
		 * Logout function (can be called from outside)
		 * @param url
		 */
		async logout(
			{ commit, state }: { commit: Commit; state: LoginState },
			options: { url: string; returnUrl?: string },
		) {
			if (options.url) {
				const action = 'logout';
				await postRequest(commit, state, options.url, action);
				if (options.returnUrl) {
					window.location.href = options.returnUrl;
				}
			}
		},
	},
};
